/*
 * recommendations.js — impression and click events for the recommendation carousels.
 *
 * Every carousel rendered by sections/recommended-products.liquid (sources related,
 * complementary and home) carries
 * data-rec-list / data-rec-section / data-rec-product on its <slider-component>,
 * and every slide carries data-rec-id / data-rec-handle / data-rec-position.
 * This script publishes two custom events through Shopify.analytics.publish:
 *
 *   dispofy_rec_view  { list_id, section_id, page_product_id, count, items: [{ id, handle, position, price }] }
 *   dispofy_rec_click { list_id, section_id, page_product_id, product_id, handle, position, action: 'link' | 'quick_add', variant_id?, rec_source }
 *
 * A custom pixel (Settings → Customer events) forwards them to GA4; the theme
 * ships no GA4 of its own. The view fires once per carousel, when half of it is
 * visible — or, for a carousel taller than two viewports (a multi-row grid: the
 * desktop slider or the mobile swipe switched off in the editor), when it covers
 * half the viewport, because its intersection ratio can never reach 0.5. Clicks
 * are delegated: product links (card image + title + swatch row + "Alege
 * Marimea") and the quick-add form submit. `quick_add` counts the submit, not
 * the cart response: an in-flight double submit is skipped (product-form.js
 * ignores it too — the button is aria-disabled while the request runs), but a
 * sold-out response or a network failure still counts.
 * Nothing runs in the theme editor.
 *
 * Idempotent (window.__dispofyRec): every section tag includes this script, so
 * the duplicate tags are no-ops; sections replaced by the editor are re-bound
 * on shopify:section:load.
 */
(function () {
	if (window.__dispofyRec) return;
	window.__dispofyRec = true;
	if (window.Shopify && window.Shopify.designMode) return;

	var CAROUSEL_SELECTOR = '[data-rec-list]';
	var SLIDE_SELECTOR = '[data-rec-id]';
	var VIEW_EVENT = 'dispofy_rec_view';
	var CLICK_EVENT = 'dispofy_rec_click';

	function publish(name, payload) {
		if (!window.Shopify || !window.Shopify.analytics || typeof window.Shopify.analytics.publish !== 'function') {
			return;
		}
		try {
			window.Shopify.analytics.publish(name, payload);
		} catch (error) {
			/* Analytics must never break the storefront. */
		}
	}

	function slideItem(slide) {
		return {
			id: slide.getAttribute('data-rec-id'),
			handle: slide.getAttribute('data-rec-handle'),
			position: Number(slide.getAttribute('data-rec-position')) || 0,
			price: Number(slide.getAttribute('data-rec-price')) || null
		};
	}

	function basePayload(carousel) {
		return {
			list_id: carousel.getAttribute('data-rec-list'),
			section_id: carousel.getAttribute('data-rec-section'),
			page_product_id: carousel.getAttribute('data-rec-product') || null
		};
	}

	function viewPayload(carousel) {
		var items = Array.prototype.map.call(carousel.querySelectorAll(SLIDE_SELECTOR), slideItem);
		var payload = basePayload(carousel);
		payload.count = items.length;
		payload.items = items;
		return payload;
	}

	function clickPayload(carousel, slide, action, variantId) {
		var payload = basePayload(carousel);
		var item = slideItem(slide);
		payload.product_id = item.id;
		payload.handle = item.handle;
		payload.position = item.position;
		payload.action = action;
		if (variantId) payload.variant_id = variantId;
		payload.rec_source = carousel.getAttribute('data-rec-list');
		return payload;
	}

	var VIEW_RATIO = 0.5;

	/*
	 * Viewed = at least half the carousel is visible, or it covers at least half the
	 * viewport. A multi-row grid taller than two viewports never reaches a 0.5
	 * intersection ratio, so the second rule is what counts it. The 0.1 threshold steps
	 * make the callback run often enough for that rule to fire close to the moment it
	 * becomes true; the observer's initial notification covers a carousel already in view.
	 */
	function isViewed(entry) {
		if (entry.intersectionRatio >= VIEW_RATIO) return true;
		var viewportHeight = entry.rootBounds ? entry.rootBounds.height : window.innerHeight;
		return viewportHeight > 0 && entry.intersectionRect.height >= viewportHeight * VIEW_RATIO;
	}

	var observer =
		'IntersectionObserver' in window
			? new IntersectionObserver(
					function (entries) {
						entries.forEach(function (entry) {
							if (!entry.isIntersecting || !isViewed(entry)) return;
							observer.unobserve(entry.target);
							publish(VIEW_EVENT, viewPayload(entry.target));
						});
					},
					{ threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5] }
				)
			: null;

	function bind(carousel) {
		if (carousel.__dispofyRecBound) return;
		carousel.__dispofyRecBound = true;

		if (observer) {
			observer.observe(carousel);
		} else {
			publish(VIEW_EVENT, viewPayload(carousel));
		}

		carousel.addEventListener('click', function (event) {
			var link = event.target.closest('a[href]');
			if (!link) return;
			var slide = link.closest(SLIDE_SELECTOR);
			if (!slide || !carousel.contains(slide)) return;
			publish(CLICK_EVENT, clickPayload(carousel, slide, 'link'));
		});

		carousel.addEventListener(
			'submit',
			function (event) {
				var form = event.target;
				if (!form || form.getAttribute('data-type') !== 'add-to-cart-form') return;
				var slide = form.closest(SLIDE_SELECTOR);
				if (!slide) return;
				/*
				 * Mirror product-form.js: while its request is in flight the button is
				 * aria-disabled (not disabled), so a double click submits again — and
				 * must not count again.
				 */
				var button = form.querySelector('[type="submit"]');
				if (button && button.getAttribute('aria-disabled') === 'true') return;
				var variant = form.querySelector('[name="id"]');
				publish(CLICK_EVENT, clickPayload(carousel, slide, 'quick_add', variant ? variant.value : null));
			},
			true
		);
	}

	function bindAll(root) {
		var scope = root && root.querySelectorAll ? root : document;
		Array.prototype.forEach.call(scope.querySelectorAll(CAROUSEL_SELECTOR), bind);
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', function () {
			bindAll(document);
		});
	} else {
		bindAll(document);
	}

	document.addEventListener('shopify:section:load', function (event) {
		bindAll(event.target);
	});
})();
